import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useState } from 'react';
import useConfirmDialog from '../hooks/useConfirmDialog';

const OrderDiscountModal = ({
  isOpen,
  onClose,
  itemsSubtotal,
  discountableSubtotal,
  orderDiscountAmount,
  orderDiscountPercentage,
  onApply,
}) => {
  if (!isOpen) return null;

  const [inputRaw, setInputRaw] = useState('0');
  const [mode, setMode] = useState('amount'); // 'amount' | 'percent'
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    if (!isOpen) return;

    const parsedAmount = orderDiscountAmount === null || orderDiscountAmount === undefined
      ? null
      : Number(orderDiscountAmount);
    const currentAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
    const parsedPercentage = orderDiscountPercentage === null
      || orderDiscountPercentage === undefined
      ? null
      : Number(orderDiscountPercentage);

    if (Number.isFinite(parsedPercentage) && parsedPercentage > 0) {
      setMode('percent');
      setInputRaw(String(parsedPercentage));
    } else if (currentAmount > 0) {
      setMode('amount');
      setInputRaw(String(currentAmount));
    } else {
      setMode('amount');
      setInputRaw('0');
    }
  }, [isOpen, itemsSubtotal, orderDiscountAmount, orderDiscountPercentage]);

  const handleInputChange = (value) => {
    const asString = value === null || value === undefined ? '' : String(value);
    const cleaned = asString.replace(/[^\d]/g, '');
    setInputRaw(cleaned === '' ? '0' : cleaned);
  };

  const subtotal = itemsSubtotal || 0;
  const eligibleSubtotal = Math.min(
    Math.max(Number(discountableSubtotal ?? subtotal) || 0, 0),
    subtotal
  );
  const numericValue = parseInt(inputRaw || '0', 10) || 0;
  const percentageClamped = Math.min(Math.max(numericValue, 0), 100);
  const percentEnabled = numericValue > 0 && numericValue < 100;
  const percentActive = mode === 'percent' && percentEnabled;

  let previewDiscount = 0;
  if (percentActive) {
    previewDiscount = Math.floor(eligibleSubtotal * (percentageClamped / 100));
  } else {
    previewDiscount = Math.min(Math.max(numericValue, 0), eligibleSubtotal);
  }

  const previewTotal = Math.max(subtotal - previewDiscount, 0);
  const previewPercentage =
    subtotal > 0 && previewDiscount > 0
      ? Math.round((previewDiscount / subtotal) * 100)
      : 0;
  const suffix = percentActive ? '%' : '$';
  const handleConfirm = async () => {
    if (subtotal <= 0) {
      onApply(null, null);
      onClose();
      return;
    }

    const finalDiscount = percentActive
      ? Math.floor(eligibleSubtotal * (percentageClamped / 100))
      : Math.min(Math.max(numericValue, 0), eligibleSubtotal);

    if (finalDiscount === subtotal) {
      const shouldApply = await confirm({
        title: 'Descuento total',
        message: '¿Estas seguro que deseas aplicar un descuento del 100%? El total quedara en $0.',
        confirmLabel: 'Aplicar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
      });
      if (!shouldApply) {
        return;
      }
    }

    if (percentActive) {
      const p = percentageClamped;
      const finalPercentage = p > 0 ? p : null;
      onApply(null, finalPercentage);
    } else {
      const amt = Math.min(Math.max(numericValue, 0), eligibleSubtotal);
      const finalAmount = amt > 0 ? amt : null;
      onApply(finalAmount, null);
    }

    onClose();
  };

  const handlePadInput = (key) => {
    let value = inputRaw;

    if (key === 'C') {
      value = '0';
      setMode('amount');
    } else if (key === '←') {
      value = value.slice(0, -1) || '0';
    } else if (/\d/.test(key)) {
      value = value === '0' ? key : value + key;
    } else if (key === '00') {
      value = value === '0' ? '0' : value + '00';
    } else if (key === '%') {
      if (percentEnabled) {
        setMode('percent');
      }
      return;
    }

    handleInputChange(value);

    if (mode === 'percent' && !percentEnabled) {
      setMode('amount');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Descuento</p>
            <h2 className="text-lg font-semibold text-gray-900">Descuento total</h2>
            <p className="text-xs text-gray-500 mt-1">
              Aplica un descuento por monto o porcentaje.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Subtotal</div>
              <div className="text-base font-semibold text-gray-900">${formatPesoAmount(subtotal)}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500">Descuento</div>
              <div className="text-base font-semibold text-red-600">
                -${formatPesoAmount(previewDiscount)}
                {previewPercentage > 0 && <> ({previewPercentage}%)</>}
              </div>
            </div>
          </div>

          <div className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-3">
            Total con descuento: <strong>${formatPesoAmount(previewTotal)}</strong>
            {eligibleSubtotal < subtotal && (
              <span className="mt-1 block text-xs text-blue-600">
                Base afecta al descuento: ${formatPesoAmount(eligibleSubtotal)}. Las promociones no se descuentan nuevamente.
              </span>
            )}
          </div>

          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              value={inputRaw}
              onChange={(e) => handleInputChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 pr-10 text-center text-xl"
            />
            <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">
              {suffix}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              1,
              2,
              3,
              4,
              5,
              6,
              7,
              8,
              9,
              'C',
              0,
              '00',
              '←',
            ].map((key, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handlePadInput(key)}
                className="py-3 text-lg rounded-lg bg-gray-100 hover:bg-gray-200"
                disabled={key === '%' && !percentEnabled}
              >
                {key}
              </button>
            ))}

            <button
              type="button"
              onClick={handleConfirm}
              className="col-span-3 py-3 bg-blue-600 text-white text-lg rounded-lg hover:bg-blue-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDiscountModal;
